import type { ReactNode } from "react";
import type { SortDir } from "@/lib/list-url";

export interface Column<T> {
  /** The server's sort key for this column. */
  key: string;
  label: string;
  /** Defaults to true. */
  sortable?: boolean;
  /** The label is announced but not shown (an actions column). */
  hideLabel?: boolean;
  align?: "right";
  render: (row: T) => ReactNode;
}

interface SortableTableProps<T> {
  caption: string;
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  sort: string;
  dir: SortDir;
  onSort: (key: string) => void;
}

const TH = "px-3 py-2 text-left font-semibold text-gray-700";
const TD = "px-3 py-2 align-top text-gray-900";

/** A lookup table whose headings sort it: click a heading to sort by that
 *  column, click again to reverse. Wide tables scroll inside their own box. */
export function SortableTable<T>({ caption, columns, rows, rowKey, sort, dir, onSort }: SortableTableProps<T>): React.ReactElement {
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
      <table className="min-w-full text-sm">
        <caption className="sr-only">{caption}</caption>
        <thead className="bg-gray-50">
          <tr>
            {columns.map((c) => {
              const active = c.key === sort;
              const label = c.hideLabel ? <span className="sr-only">{c.label}</span> : c.label;
              return (
                <th
                  key={c.key}
                  scope="col"
                  aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : undefined}
                  className={`${TH} ${c.align === "right" ? "text-right" : ""}`}
                >
                  {c.sortable === false ? (
                    label
                  ) : (
                    <button
                      type="button"
                      onClick={() => onSort(c.key)}
                      className="inline-flex items-center gap-1 font-semibold hover:text-gray-900"
                    >
                      {label}
                      <span aria-hidden="true" className="text-xs text-blue-700">
                        {active ? (dir === "asc" ? "▲" : "▼") : ""}
                      </span>
                    </button>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={rowKey(row)} className="border-t border-gray-100">
              {columns.map((c) => (
                <td key={c.key} className={`${TD} ${c.align === "right" ? "text-right" : ""}`}>
                  {c.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
